import React, { useEffect, useRef } from "react";
import { SectionContent } from "./style";
import {
  BlankImage,
  Discord,
  Globe,
  Instagram,
  LeafIcon,
  OwnedBy,
  Xtwitter,
} from "../../shared/icons";
import { Skeleton } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import { screenSizes } from "../../styles/theme";
import { showToast } from "../../slices/global/ToastSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import ReadMoreParagraph from "../../components/New/ReadMoreParagraph";
import { getValidImageUrl } from "../../utils";

const HeroSection = styled.section`
  background-size: cover;
  padding: 30px 60px;
  height: auto;
  margin-top: 55px;
  border-bottom: 1px solid #091c001a;
  @media (max-width: ${screenSizes.L}px) {
    height: auto;
    padding: 6rem 1rem 2rem 1rem;
    background: none;
    margin-top: 0;
  }
`;

const ContentSection = styled.div`
  padding: 30px 100px;
  display: flex;
  column-gap: 80px;
  img {
    height: 520px;
    width: 520px;
    background-size: cover;
    object-fit: contain;
    background-position: center;
    @media (max-width: ${screenSizes.L}px) {
      height: auto;
      width: auto;
      margin-bottom: 30px;
    }
  }
  @media (max-width: ${screenSizes.L}px) {
    flex-direction: column;
    padding: 0;
  }
`;

const InfoSection = styled.div`
  flex: 1;
  h6 {
    margin: 0;
    margin-bottom: 15px;
    font-weight: 400;
    font-size: 16px;
    color: #fff;
    margin-top: 0;
    line-height: 150%;
    span {
      position: relative;
      padding-left: 15px;
      &:before {
        content: "";
        position: absolute;
        left: 0;
        top: 5px;
        background: #fff;
        width: 8px;
        height: 8px;
        border-radius: 50px;
      }
    }
  }

  h2 {
    margin: 0;
    margin-bottom: 5px;
    display: flex;
    flex-direction: column;
    font-weight: 700;
    font-size: 48px;
    line-height: 56px;
    color: #091c00;
    font-family: Manrope, sans-serif;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    @media (max-width: ${screenSizes.L}px) {
      font-size: 32px;
      line-height: 40px;
      color: #091c00;
    }
  }

  div {
    display: flex;
    margin-bottom: 5px;
    align-items: center;
    justify-content: space-between;
    @media (max-width: ${screenSizes.L}px) {
      margin-bottom: 8px;
      color: #091c00;
      gap: 10px;
      align-items: center;
    }
    .hover-container {
      display: flex;
      align-items: center;
      cursor: pointer;
      @media (max-width: ${screenSizes.L}px) {
      margin-block: 10px;
    }
    }

    .hover-image {
      height: 36px;
      width: 36px;
    }

    .hover-link {
      color: #091c00;
      text-decoration: underline;
      font-size: 16px;
      font-weight: 600;
    }

    .hover-container:hover .hover-image {
      filter: invert(68%) sepia(64%) saturate(370%) hue-rotate(344deg);
    }

    .hover-container:hover .hover-link {
      color: #ff9d59;
    }
    div {
      margin-right: 10px;
      display: flex;
      justify-content: left;
      @media (max-width: ${screenSizes.L}px) {
        margin-right: 0;
      }
      img {
        width: 40px;
        height: 40px;
        margin-right: 10px;
        @media (max-width: ${screenSizes.L}px) {
          width: 32px;
          height: 32px;
          margin-block: auto;
        }
      }

      p {
        font-size: 16px;
        font-weight: 600;
        color: #091c00;
        @media (max-width: ${screenSizes.M}px) {
          font-size: 14px;
        }
        span {
          cursor: pointer;
        }
      }
    }
  }

  p {
    font-size: 16px;
    font-weight: 400;
    color: #091c00;
    line-height: 24px;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  div {
    button {
      margin-top: 30px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      outline: none;
      background-color: #ff9d59;
      padding: 16px 20px;
      color: #fff;
      border-radius: 80px;
      gap: 10px;
      font-size: 18px;
      font-weight: 600;
      &:hover {
        background-color: #e0772d;
      }
      span {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
`;

const SocialIconsWrapper = styled.div`
  margin-top: 30px;
  display: flex;
  align-items: center;
  column-gap: 10px;
  color: #091c00;
  @media (max-width: ${screenSizes.L}px) {
    justify-content: center !important;
    margin-top: 30px;
    margin-inline: 1rem;
    width: 100%;
  }
  span {
    font-size: 14px;
    font-weight: 600;
    color: #091c00;
  }

  img {
    cursor: pointer;
    height: 24px !important;
    width: 24px !important;
    filter: brightness(0) saturate(100%) sepia(100%) hue-rotate(135deg)
      saturate(800%) contrast(90%);
    &:hover {
      filter: invert(42%) sepia(65%) saturate(334%) hue-rotate(344deg);
    }
  }
`;

const Rating = styled.div<{ value: number }>`
  display: flex;
  align-items: center;
  background-color: ${({ value }) =>
    value <= 3 ? "#C7FFC5" : value >= 6 ? "#FFC5C5" : "#feffc5"};
  border: 1px solid rgba(9, 28, 0, 0.1);
  padding: 0 8px;
  border-radius: 40px;
  justify-content: center;
  span {
    font-size: 14px;
    font-weight: 400;
    color: #091c00;
    @media (max-width: ${screenSizes.L}px) {
      margin-left: 0;
    }
  }
`;

const UnitAndScore = styled.div`
  width: 100%;
  display: flex !important;
  justify-content: flex-start !important;
  div {
    &:first-child {
      padding: 2px 16px;
      border: 1px solid #091c00;
      border-radius: 40px;
      display: inline-flex;
      font-size: 12px;
      font-weight: 500;
      color: #091c00;
    }
  }
`;

interface Props {
  collectionDetails: {
    featureImage: string | null | undefined;
    heading: string;
    creator: string;
    owner: string;
    description: string;
    chain?: any;
    score?: any;
    externalLink?: string;
  } | null;
  isLoading: boolean;
}

const CollectionHeroSection: React.FC<Props> = ({
  collectionDetails,
  isLoading,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();

  const copyToClipboard = (url: string, data: string) => {
    navigator.clipboard.writeText(url).then(() => {
      dispatch(
        showToast({
          message: `${data} copied to clipboard!`,
          severity: "success",
        })
      );
    });
  };

  const appUrl = `https://vyga-dev-aggregator.web.app${location?.pathname}`;
  const handleShare = (platform: string) => {
    let shareUrl = "";
    switch (platform) {
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          appUrl
        )}&text=Check out this awesome app!`;
        break;
      case "discord":
        shareUrl = `https://discord.com/channels/@me`;
        copyToClipboard(appUrl, "URL");
        break;
      case "instagram":
        copyToClipboard(appUrl, "URL");
        return;
      default:
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank");
    }
  };

  const params = useParams();
  const prevParams = useRef(params);

  useEffect(() => {
    if (JSON.stringify(prevParams.current) !== JSON.stringify(params)) {
      window.location.reload();
    }
    prevParams.current = params;
  }, [params]);

  return (
    <>
      <HeroSection>
        {isLoading ? (
          <ContentSection>
            <Skeleton variant="rectangular" width={500} height={500} />
            <SectionContent>
              <div style={{ marginBottom: "20px" }}>
                <Skeleton variant="rectangular" width={120} height={20} />
              </div>
              <h1 style={{ marginBottom: "20px" }}>
                <Skeleton variant="rectangular" width={400} height={80} />
              </h1>
              <div style={{ marginBottom: "20px" }}>
                <Skeleton variant="rectangular" width={450} height={30} />
              </div>
              <div style={{ marginBottom: "20px" }}>
                <Skeleton variant="rectangular" width={600} height={200} />
              </div>
              <div>
                <Skeleton variant="rectangular" width={420} height={30} />
              </div>
            </SectionContent>
          </ContentSection>
        ) : (
          <ContentSection>
            <img
              src={getValidImageUrl(
                collectionDetails?.featureImage ?? "",
                BlankImage
              )}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = BlankImage;
              }}
              alt="nft_image"
            />
            <InfoSection>
              <UnitAndScore>
                <div>{collectionDetails?.chain}</div>
                <Rating value={collectionDetails?.score || 1}>
                  <LeafIcon />
                  <span>
                    {typeof collectionDetails?.score === "number"
                      ? collectionDetails.score.toFixed(1)
                      : "0"}
                  </span>
                </Rating>
              </UnitAndScore>

              <h2 title={collectionDetails?.heading}>
                {collectionDetails?.heading}
              </h2>
              <div>
                {/*}
                {collectionDetails?.creator && (
                  <div>
                    <img
                      src={OwnedBy}
                      alt=""
                      style={{
                        border: "2px solid #FF9D59",
                        borderRadius: "40px",
                      }}
                    />
                    
                    <p>
                      Created by |{" "}
                      <span
                        onClick={() =>
                          copyToClipboard(collectionDetails?.creator, "ID")
                        }
                        title={collectionDetails?.creator}
                      >
                        {collectionDetails?.creator.length>15 ? truncateId(collectionDetails?.creator) : collectionDetails?.creator}
                      </span>
                    </p>
                    
                  </div>
                )}
                  {*/}
                {collectionDetails?.externalLink && (
                  <div className="hover-container">
                    <img src={Globe} alt="" className="hover-image" />
                    <a
                      href={collectionDetails?.externalLink || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="hover-link"
                    >
                      Original Page Link
                    </a>
                  </div>
                )}
              </div>
              {collectionDetails?.description &&
                collectionDetails?.description?.length > 0 && (
                  <ReadMoreParagraph content={collectionDetails?.description} />
                )}

              <div>
                <SocialIconsWrapper>
                  <span>Share on:</span>
                  <img
                    src={Instagram}
                    alt="icon"
                    onClick={() => handleShare("instagram")}
                  />
                  <img
                    src={Discord}
                    alt="icon"
                    onClick={() => handleShare("discord")}
                  />
                  <img
                    src={Xtwitter}
                    alt="icon"
                    onClick={() => handleShare("twitter")}
                  />
                </SocialIconsWrapper>
              </div>
            </InfoSection>
          </ContentSection>
        )}
      </HeroSection>
    </>
  );
};

export default CollectionHeroSection;
